import KSUID from 'ksuid-edge';


// This should match your backend ChatMemory.ENTITY_PREFIX
// You may need to adjust this value to match what's defined in your backend
const CHAT_MEMORY_ENTITY_PREFIX = 'chm_';
const MESSAGE_ENTITY_PREFIX = 'msg_';

const DOCUMENT_ENTITY_PREFIX = 'doc_';
/** Lineage edge ID prefix (lnk_); matches backend LineageEdge.ENTITY_PREFIX. */
const LINEAGE_EDGE_ENTITY_PREFIX = 'lnk_';
const EXPERIMENT_ENTITY_PREFIX = 'exp_';
/** Backend User.ENTITY_PREFIX */
export const USER_ENTITY_PREFIX = 'usr_';
/** Backend Project.ENTITY_PREFIX */
export const PROJECT_ENTITY_PREFIX = 'prj_';
export { EXPERIMENT_ENTITY_PREFIX };
/** Assumption ID prefix (asm_); matches backend Assumption.ENTITY_PREFIX. */
const ASSUMPTION_ENTITY_PREFIX = 'asm_';
/** Hypothesis ID prefix (hyp_); matches backend Hypothesis.ENTITY_PREFIX. */
const HYPOTHESIS_ENTITY_PREFIX = 'hyp_';
/** Conversation ID prefix (cnv_); matches backend Conversation.ENTITY_PREFIX. */
export const CONVERSATION_ENTITY_PREFIX = 'cnv_';
export { ASSUMPTION_ENTITY_PREFIX, HYPOTHESIS_ENTITY_PREFIX };
/** Key metric ID prefix (kms_); matches backend EntityIdGenerationTools. */
const KEY_METRIC_ENTITY_PREFIX = 'kms_';
/** Matches backend Insight.ENTITY_PREFIX */
const LEARNING_ANNOTATION_ENTITY_PREFIX = 'lan_';
/** Source import batch ID prefix (sib_); matches backend SourceImportBatch.ENTITY_PREFIX. */
const SOURCE_IMPORT_BATCH_ENTITY_PREFIX = 'sib_';

/** Backend Workspace.ENTITY_PREFIX; use when frontend creates workspace before API call if backend accepts it. */
export const WORKSPACE_ENTITY_PREFIX = 'wks_';
/** Backend Group.ENTITY_PREFIX; use when frontend creates group before API call if backend accepts it. */
export const GROUP_ENTITY_PREFIX = 'grp_';


/**
 * Generates a unique chat memory ID using KSUID with the same prefix as backend
 * This ensures consistency between frontend and backend ID generation
 * 
 * @returns A unique chat memory ID string
 */
export const generateChatMemoryId = (): string => {
  const ksuid = KSUID.randomSync();
  return `${CHAT_MEMORY_ENTITY_PREFIX}${ksuid.string}`;
};

/**
 * Generates a unique message ID using KSUID with the msg_ prefix
 * This matches the backend Message entity prefix
 * 
 * @returns A unique message ID string with msg_ prefix
 */
export const generateMessageId = (): string => {
  const ksuid = KSUID.randomSync();
  return `${MESSAGE_ENTITY_PREFIX}${ksuid.string}`;
};


export const generateDocumentId = (): string => {
    const ksuid = KSUID.randomSync();
    return `${DOCUMENT_ENTITY_PREFIX}${ksuid.string}`;
};

/**
 * Generates a unique lineage edge ID with lnk_ prefix.
 *
 * @returns A unique lineage edge ID string
 */
export const generateLineageEdgeId = (): string => {
  const ksuid = KSUID.randomSync();
  return `${LINEAGE_EDGE_ENTITY_PREFIX}${ksuid.string}`;
};

/**
 * Generates a unique experiment ID (exp_ prefix, matches backend Experiment.ENTITY_PREFIX).
 * Use when creating an experiment via POST /experiments.
 */
export const generateExperimentId = (): string => {
  const ksuid = KSUID.randomSync();
  return `${EXPERIMENT_ENTITY_PREFIX}${ksuid.string}`;
};

/** Use when creating an assumption via POST /assumptions. */
export const generateAssumptionId = (): string => {
  const ksuid = KSUID.randomSync();
  return `${ASSUMPTION_ENTITY_PREFIX}${ksuid.string}`;
};

/** Use when creating a hypothesis via POST /hypotheses. */
export const generateHypothesisId = (): string => {
  const ksuid = KSUID.randomSync();
  return `${HYPOTHESIS_ENTITY_PREFIX}${ksuid.string}`;
};

/**
 * Generates a unique key metric ID (kms_ prefix, matches backend EntityIdGenerationTools).
 * Use when adding a new key metric in experiment create or edit.
 */
export const generateKeyMetricId = (): string => {
  const ksuid = KSUID.randomSync();
  return `${KEY_METRIC_ENTITY_PREFIX}${ksuid.string}`;
};

/**
 * Generates a unique learning annotation ID using KSUID with the same prefix as backend
 * This ensures consistency between frontend and backend ID generation
 * 
 * @returns A unique learning annotation ID string
 */
export const generateLearningAnnotationId = (): string => {
  const ksuid = KSUID.randomSync();
  return `${LEARNING_ANNOTATION_ENTITY_PREFIX}${ksuid.string}`;
};

/**
 * Generates a workspace ID with wks_ prefix (matches backend Workspace.ENTITY_PREFIX).
 * Use only when frontend creates the entity before the API call and backend accepts client-generated id.
 */
export const generateWorkspaceId = (): string => {
  const ksuid = KSUID.randomSync();
  return `${WORKSPACE_ENTITY_PREFIX}${ksuid.string}`;
};

/**
 * Generates a group ID with grp_ prefix (matches backend Group.ENTITY_PREFIX).
 * Use only when frontend creates the entity before the API call and backend accepts client-generated id.
 */
export const generateGroupId = (): string => {
  const ksuid = KSUID.randomSync();
  return `${GROUP_ENTITY_PREFIX}${ksuid.string}`;
};

/**
 * Generates a source import batch ID (sib_ prefix).
 * Use when creating an import batch via POST .../import-batches.
 */
export const generateSourceImportBatchId = (): string => {
  const ksuid = KSUID.randomSync();
  return `${SOURCE_IMPORT_BATCH_ENTITY_PREFIX}${ksuid.string}`;
};

/**
 * Validates if a string is a valid chat memory ID format
 * 
 * @param id The ID string to validate
 * @returns True if the ID has the correct format
 */
export const isValidChatMemoryId = (id: string): boolean => {
  return Boolean(id && id.startsWith(CHAT_MEMORY_ENTITY_PREFIX) && id.length > CHAT_MEMORY_ENTITY_PREFIX.length);
};

/**
 * Validates a public ID with a known entity prefix and KSUID body.
 *
 * @param id Full public ID including prefix
 * @param prefix Entity prefix (e.g. exp_, usr_, prj_)
 * @returns True when prefix matches and the 27-character suffix parses as KSUID
 */
export function isValidPrefixedKsuid(id: string, prefix: string): boolean {
  if (!id.startsWith(prefix) || id.length !== prefix.length + 27) {
    return false;
  }
  try {
    KSUID.parse(id.slice(prefix.length));
    return true;
  } catch {
    return false;
  }
}

/**
 * Generates a KSUID string for general use
 * 
 * @returns A unique KSUID string
 */
export const generateKsuid = (): string => {
  const ksuid = KSUID.randomSync();
  return ksuid.string;
};
